import React, { useState, useEffect } from 'react';
import { usersAPI, referencesAPI } from '../services/api';
import './ProfilePage.css';

const ProfilePage = ({ onBack }) => {
  const [profile, setProfile] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    organization_id: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    newPassword: ''
  });

  const isSuperuser = localStorage.getItem('userRole') === 'superuser';

  useEffect(() => {
    loadProfile();
    loadOrganizations();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await usersAPI.getMe();
      setProfile(data);
      setFormData({
        username: data.username || '',
        email: data.email || '',
        phone: data.phone || '',
        organization_id: data.organizationId || ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка загрузки профиля');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const data = await referencesAPI.getOrganizations();
      setOrganizations(data);
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const data = {
        username: formData.username || undefined,
        email: formData.email || null,
        phone: formData.phone || null,
        organization_id: formData.organization_id || null
      };
      const updated = await usersAPI.updateMe(data);
      setProfile(updated);
      if (formData.username !== profile.username) {
        localStorage.setItem('userRole', updated.role);
      }
      setSuccess('Профиль успешно обновлён');
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения профиля');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!passwordData.newPassword) {
      setError('Введите новый пароль');
      return;
    }

    setSaving(true);

    try {
      await usersAPI.changePassword(passwordData.newPassword);
      setSuccess('Пароль успешно изменён');
      setPasswordData({ newPassword: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка смены пароля');
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      superuser: 'Супер администратор',
      admin: 'Администратор',
      manager: 'Руководитель',
      user: 'Пользователь'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="loading">Загрузка...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h2>Мой профиль</h2>
          <button onClick={onBack} className="back-button">Назад</button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="profile-content">
          <div className="profile-info">
            <h3>Информация о пользователе</h3>
            <div className="info-row">
              <span className="info-label">Роль:</span>
              <span className="info-value">{getRoleLabel(profile?.role)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Дата регистрации:</span>
              <span className="info-value">{profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('ru-RU') : '-'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Организация:</span>
              <span className="info-value">{profile?.organizationName || '-'}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="profile-form">
            <h3>Редактирование профиля</h3>
            
            <div className="form-group">
              <label>Имя пользователя:</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@mail.ru"
              />
            </div>

            <div className="form-group">
              <label>Телефон:</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+7 (999) 123-45-67"
              />
            </div>

            {isSuperuser && (
              <div className="form-group">
                <label>Организация:</label>
                <select
                  name="organization_id"
                  value={formData.organization_id}
                  onChange={handleChange}
                >
                  <option value="">Не выбрано</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button type="submit" className="submit-button" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
          </form>

          <form onSubmit={handlePasswordSubmit} className="password-form">
            <h3>Смена пароля</h3>
            
            <div className="form-group">
              <label>Новый пароль:</label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder="Введите новый пароль"
              />
            </div>

            <button type="submit" className="submit-button" disabled={saving}>
              {saving ? 'Сохранение...' : 'Изменить пароль'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
